import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, InjectionToken, Optional, PLATFORM_ID } from '@angular/core';

declare global {
  interface Window {
    grecaptcha?: {
      ready(cb: () => void): void;
      execute(siteKey: string, options: { action: string }): Promise<string>;
    };
  }
}

/**
 * Provide the app's own `environment.recaptchaSiteKey` under this token
 * (e.g. in `app.config.ts`: `{ provide: RECAPTCHA_SITE_KEY, useValue: environment.recaptchaSiteKey }`).
 * Each app keeps its own environment file, so the key can't be read from a
 * shared lib directly.
 */
export const RECAPTCHA_SITE_KEY = new InjectionToken<string>('RECAPTCHA_SITE_KEY');

/**
 * Loads Google reCAPTCHA v3's script and executes it invisibly on demand.
 * SSR-safe: does nothing on the server. Shared by every app's auth forms
 * (client/executor/admin) - originally written for the marketing site's
 * contact form (projects/marketing/src/app/core/services/recaptcha.service.ts),
 * moved here so the auth forms don't duplicate it.
 */
@Injectable({ providedIn: 'root' })
export class RecaptchaService {
  private scriptLoaded = false;
  private loadingPromise: Promise<void> | null = null;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: object,
    @Optional() @Inject(RECAPTCHA_SITE_KEY) private readonly siteKey: string | null,
  ) {}

  /** True once a real site key is configured for this app - lets callers skip execute() entirely in dev. */
  isConfigured(): boolean {
    return !!this.siteKey;
  }

  private loadScript(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !this.siteKey) {
      return Promise.resolve();
    }
    if (this.scriptLoaded) {
      return Promise.resolve();
    }
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => reject(new Error('Failed to load reCAPTCHA script'));
      document.head.appendChild(script);
    });

    return this.loadingPromise;
  }

  /** Resolves to '' on the server, or if no site key is configured (local dev default). */
  async execute(action: string): Promise<string> {
    if (!isPlatformBrowser(this.platformId) || !this.siteKey) {
      return '';
    }
    await this.loadScript();
    return new Promise((resolve, reject) => {
      window.grecaptcha?.ready(() => {
        window.grecaptcha!.execute(this.siteKey!, { action }).then(resolve).catch(reject);
      });
    });
  }
}
